import React, { useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
    Chart,
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { formatFinancialUnitValue } from '../utils/financialUnit';

Chart.register(
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend
);

const PeerChartModal = ({
    isOpen,
    asInline = false, // <-- new prop
    onClose,
    selectedPeerRatio,
    allRatios,
    selectedPeers,
    companyName,
    baseCompanyRatios
}) => {
    const [chartType, setChartType] = useState('line');

    const getCssVar = (name, fallback) => {
        const val = getComputedStyle(document.body).getPropertyValue(name).trim();
        return val || fallback;
    };

    const hexToRgba = (hex, alpha) => {
        if (!hex) return `rgba(0, 0, 0, ${alpha})`;
        if (hex.startsWith('rgb')) return hex.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
        const clean = hex.replace('#', '');
        const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
        const num = parseInt(full, 16);
        const r = (num >> 16) & 255;
        const g = (num >> 8) & 255;
        const b = num & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    if (!isOpen || !selectedPeerRatio) return null;

    // Find the base company ratio object
    const baseRatio = allRatios.find(r => r.name === selectedPeerRatio);

    // Get all years from base company ratio (assume all companies have same years)
    const years = baseRatio?.values?.map(v => v.year) || [];

    // Helper to get ratio values for a company for all years
    const getCompanyData = (ratiosObj) => {
        if (!ratiosObj || !ratiosObj[selectedPeerRatio]) return years.map(() => null);
        const ratioVals = ratiosObj[selectedPeerRatio];
        // If ratioVals is an array of {year, value}
        if (Array.isArray(ratioVals)) {
            const yearMap = Object.fromEntries(ratioVals.map(v => [v.year, v.value]));
            return years.map(y => yearMap[y] !== undefined ? parseFloat(ratioVals.find(v => v.year === y)?.value) : null);
        }
        // If ratioVals is an object {year: value}
        if (typeof ratioVals === 'object') {
            return years.map(y => ratioVals[y] !== undefined ? parseFloat(ratioVals[y]) : null);
        }
        return years.map(() => null);
    };

    // Prepare datasets: base company + each peer
    const datasets = [
        {
            label: companyName,
            data: getCompanyData(baseCompanyRatios || { [selectedPeerRatio]: baseRatio?.values }),
            borderColor: getCssVar('--chart-blue', '#3B82F6'),
            backgroundColor: hexToRgba(getCssVar('--chart-blue', '#3B82F6'), 0.5),
            fill: false,
            tension: 0.2,
        },
        ...selectedPeers.map((peer, idx) => ({
            label: peer.companyName,
            data: getCompanyData(peer.ratios),
            borderColor: `hsl(${(idx * 60 + 120) % 360}, 70%, 50%)`,
            backgroundColor: `hsl(${(idx * 60 + 120) % 360}, 70%, 60%)`,
            fill: false,
            tension: 0.2,
        }))
    ];

    const chartData = {
        labels: years,
        datasets
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { display: true },
            valueLabels: false,
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (ctx) => {
                        const formatted = formatFinancialUnitValue(ctx.parsed.y, baseRatio?.unit, {
                            name: baseRatio?.name || selectedPeerRatio,
                            displayName: baseRatio?.displayName,
                        });
                        return `${ctx.dataset.label}: ${formatted || '—'}`;
                    },
                },
            },
        },
        scales: {
            x: { title: { display: true, text: 'Year' } },
            y: {
                title: { display: true, text: baseRatio?.displayName || selectedPeerRatio },
                ticks: {
                    callback: (value) => formatFinancialUnitValue(value, baseRatio?.unit, {
                        name: baseRatio?.name || selectedPeerRatio,
                        displayName: baseRatio?.displayName,
                    }),
                },
            },
        },
    };

    const ChartComponent = chartType === 'bar' ? Bar : Line;

    // Inline rendering (no modal overlay, no close button)
    if (asInline) {
        return (
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, height: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <select
                        value={chartType}
                        onChange={e => setChartType(e.target.value)}
                        style={{ padding: '6px', background: 'var(--input-bg)', color: 'var(--text-1)', border: '1px solid var(--border)', borderRadius: 6 }}
                    >
                        <option value="line">📈 Line Chart</option>
                        <option value="bar">📊 Bar Chart</option>
                    </select>
                </div>
                <div className="chart-container" style={{ height: 'calc(100% - 60px)', marginTop: '20px' }}>
                    <ChartComponent data={chartData} options={chartOptions} />
                </div>
            </div>
        );
    }

    // Modal rendering (default, for backward compatibility)
    return (
        <div className="ratio-modal-overlay">
            <div className="ratio-modal" style={{ width: '80%', height: '80%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <select value={chartType} onChange={e => setChartType(e.target.value)} style={{ padding: '6px' }}>
                        <option value="line">📈 Line Chart</option>
                        <option value="bar">📊 Bar Chart</option>
                    </select>
                </div>
                <div className="chart-container" style={{ height: 'calc(100% - 100px)', marginTop: '20px' }}>
                    <ChartComponent data={chartData} options={chartOptions} />
                </div>
                <button className="close-button" onClick={onClose} style={{ marginTop: '10px' }}>
                    Close
                </button>
            </div>
        </div>
    );
};

export default PeerChartModal;  